import { Suspense, useMemo } from 'react';
import { getLayoutForRole } from '../lib/layout-config';
import { getWidgetsByRole, type UserRole } from '../lib/widget-registry';
import { HybridDashboardLayout } from './HybridDashboardLayout';
import { ScannerFocusLayout } from './ScannerFocusLayout';

export interface RoleBasedDashboardProps {
  userRole: UserRole | null;
  className?: string;
}

/**
 * Role-Based Dashboard Container (Epic 35: Story 35-1, Story 35-8)
 * Orchestrates layout selection, widget filtering, and lazy loading based on user role
 *
 * Layout selection:
 * - OPERATOR: ScannerFocusLayout (minimal UI for quick scanning)
 * - STORE_MANAGER/ADMIN: HybridDashboardLayout (collapsible sections with Executive Summary)
 */
export function RoleBasedDashboard({ userRole, className }: RoleBasedDashboardProps) {
  // Get layout type for role
  const layoutType = getLayoutForRole(userRole);

  // Get filtered widgets for role (only needed for ScannerFocusLayout)
  const widgetConfigs = useMemo(() => getWidgetsByRole(userRole), [userRole]);

  // For scanner-focus layout, render widgets with Suspense boundary
  const widgets = useMemo(
    () =>
      widgetConfigs.map(config => {
        const WidgetComponent = config.component;

        return (
          <Suspense
            key={config.id}
            fallback={
              <div className="flex items-center justify-center p-6 border border-gray-200 dark:border-gray-700 rounded-lg animate-pulse">
                <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded w-full" />
              </div>
            }
          >
            <WidgetComponent />
          </Suspense>
        );
      }),
    [widgetConfigs]
  );

  // HybridDashboardLayout handles its own widget rendering via hooks
  if (layoutType === 'dashboard-first') {
    return (
      <div className={className}>
        <HybridDashboardLayout />
      </div>
    );
  }

  // ScannerFocusLayout for operators
  return (
    <div className={className}>
      <ScannerFocusLayout widgets={widgets} />
    </div>
  );
}
